import { useMemo } from "react";
import { useIntl } from "@leagueplatform/locales";
import { useForm } from "@leagueplatform/web-common";
import { StackLayout, Box } from "@leagueplatform/genesis-core";
import { FormContainer } from "components/forms/form-container/form-container.component";
import { RHFTextInput } from "components/form-elements/rhf-text-input.component";
import { RHFSelect } from "components/form-elements/rhf-select.component";
import { SEX_OPTIONS, sexLabel } from "common/constants/form-options";
import {
  nameValidation,
  dateOfBirthValidation,
} from "common/utils/rhf-form-validation.util";
import type { ProfileData } from "types/types";

export interface PersonalInfoSectionProps {
  data: ProfileData | undefined;
  onSubmit: (values: PersonalInfoFormValues) => Promise<void>;
}

export type PersonalInfoFormValues = {
  firstName: string;
  lastName: string;
  preferredName: string;
  dateOfBirth: string;
  sex: string;
};

export const PersonalInfoSection = ({
  data,
  onSubmit,
}: PersonalInfoSectionProps) => {
  const { formatMessage } = useIntl();

  // Native `<input type="date">` uses ISO `YYYY-MM-DD` for its underlying
  // value (browsers render the localized format to the user). Keep the ISO
  // shape only for the form default so the picker round-trips correctly.
  const isoBirthDate = data?.dateOfBirth
    ? (() => {
        const date = new Date(data.dateOfBirth);
        const month = String(date.getUTCMonth() + 1).padStart(2, "0");
        const day = String(date.getUTCDate()).padStart(2, "0");
        const year = date.getUTCFullYear();
        return `${year}-${month}-${day}`;
      })()
    : undefined;

  // For the read-only display in `profileFieldValues`, format the date in
  // the user's locale (via `Intl.DateTimeFormat`). That way en-US sees
  // `06/15/1990` and es-US sees `15/06/1990` — the `<input type="date">`
  // locale rendering only applies to the picker itself, not to plain text.
  const displayBirthDate = data?.dateOfBirth
    ? (() => {
        const parsed = new Date(data.dateOfBirth);
        if (Number.isNaN(parsed.getTime())) return data.dateOfBirth;
        return new Intl.DateTimeFormat(undefined, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          timeZone: "UTC",
        }).format(parsed);
      })()
    : undefined;

  const defaultValues: PersonalInfoFormValues = useMemo(
    () => ({
      firstName: data?.firstName || "",
      lastName: data?.lastName || "",
      preferredName: data?.preferredName || "",
      dateOfBirth: isoBirthDate || "",
      sex: data?.selfIdentifiedGender || "",
    }),
    [data, isoBirthDate]
  );

  const formMethods = useForm<PersonalInfoFormValues>({
    defaultValues,
  });

  // Cap the picker at today so future dates can't be selected in the calendar
  // itself (the 18+ validator still catches younger DOBs; this is a UX
  // improvement so the picker never surfaces an obviously-invalid future date).
  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const isFirstNameLocked = data?.isFirstNameLocked ?? false;
  const isLastNameLocked = data?.isLastNameLocked ?? false;
  const isBirthDateLocked = data?.isBirthDateLocked ?? false;

  const profileFieldValues = useMemo(() => {
    if (!data) return [];

    return [
      {
        label: formatMessage({ id: "STR_FIRST_NAME" }),
        value: data.firstName,
        isLocked: data.isFirstNameLocked,
      },
      {
        label: formatMessage({ id: "STR_LAST_NAME" }),
        value: data.lastName,
        isLocked: data.isLastNameLocked,
      },
      {
        label: formatMessage({ id: "STR_PREFERRED_NAME" }),
        value: data.preferredName,
        isLocked: false,
      },
      {
        label: formatMessage({ id: "STR_BIRTH_DATE" }),
        value: displayBirthDate,
        isLocked: data.isBirthDateLocked,
      },
      {
        label: formatMessage({ id: "STR_SEX" }),
        value: sexLabel(data.selfIdentifiedGender),
        isLocked: false,
      },
    ];
  }, [data, formatMessage, displayBirthDate]);

  return (
    <FormContainer
      heading={formatMessage({ id: "STR_PERSONAL_INFORMATION" })}
      profileFieldValues={profileFieldValues}
      formMethods={formMethods}
      onSubmit={onSubmit}
      defaultValues={defaultValues}
    >
      <StackLayout spacing="$one" horizontalAlignment="stretch">
        {/* Row 1: First Name + Last Name */}
        <Box
          css={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "$one",
            "@mobile": { gridTemplateColumns: "1fr" },
          }}
        >
          <RHFTextInput
            id="firstName"
            label={formatMessage({ id: "STR_FIRST_NAME" })}
            required
            disabled={isFirstNameLocked}
            registerOptions={nameValidation(
              formatMessage({ id: "STR_INVALID_FIRST_NAME" }),
              formatMessage({ id: "STR_NAME_INVALID_CHARACTERS" })
            )}
          />
          <RHFTextInput
            id="lastName"
            label={formatMessage({ id: "STR_LAST_NAME" })}
            required
            disabled={isLastNameLocked}
            registerOptions={nameValidation(
              formatMessage({ id: "STR_INVALID_LAST_NAME" }),
              formatMessage({ id: "STR_NAME_INVALID_CHARACTERS" })
            )}
          />
        </Box>
        {/* Row 2: Preferred Name + Birth Date */}
        <Box
          css={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "$one",
            "@mobile": { gridTemplateColumns: "1fr" },
          }}
        >
          <RHFTextInput
            id="preferredName"
            label={formatMessage({ id: "STR_PREFERRED_NAME" })}
            registerOptions={nameValidation(
              formatMessage({ id: "STR_INVALID_PREFERRED_NAME" }),
              formatMessage({ id: "STR_NAME_INVALID_CHARACTERS" })
            )}
          />
          <RHFTextInput
            id="dateOfBirth"
            type="date"
            autoComplete="bday"
            max={todayIso}
            label={formatMessage({ id: "STR_BIRTH_DATE" })}
            required
            disabled={isBirthDateLocked}
            registerOptions={dateOfBirthValidation(
              formatMessage({ id: "STR_MUST_BE_18_OR_OLDER" }),
              formatMessage({ id: "STR_INVALID_BIRTH_DATE" })
            )}
          />
        </Box>
        {/* Row 3: Sex (single column) */}
        <Box
          css={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "$one",
            "@mobile": { gridTemplateColumns: "1fr" },
          }}
        >
          <RHFSelect
            id="sex"
            label={formatMessage({ id: "STR_SEX" })}
            options={SEX_OPTIONS}
            placeholder={formatMessage({ id: "STR_SELECT_SEX" })}
            required
          />
        </Box>
      </StackLayout>
    </FormContainer>
  );
};
